import { NoData } from "@/components/common/section-components/NoData";
import DatePicker from "@/components/date-picker";
import { LinearProgress } from "@/components/progress";
import { AppColors } from "@/styles/colors";
import { formatCurrency } from "@/utils/format-currency";
import { isEmpty } from "@/utils/query-string";
import { Box, Button, Card, Flex, Table, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import { Filter } from "lucide-react";
import {
  Control,
  FieldValues,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { v4 } from "uuid";

import "@/components/common/section-components/card/text-elements.css";
import { GeneralFilterAlert } from "@/components/common/section-components/alert/GeneralFilterAlert";

interface Props {
  loadingPrices: boolean;
  sectionData?: any;
  control?: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
}

const TABLE_CONFIG: Record<
  string,
  { title: string; columns: { key: string; label: string }[] }
> = {
  preciosMercado: {
    title: "Precio Mercado",
    columns: [
      { key: "fecha", label: "Fecha" },
      { key: "precioSoja", label: "Soja" },
      { key: "precioMaiz", label: "Maíz" },
      { key: "precioTrigo", label: "Trigo" },
    ],
  },
  preciosUnion: {
    title: "Precio Unión",
    columns: [
      { key: "fecha", label: "Fecha" },
      { key: "precioSoja", label: "Soja" },
      { key: "precioMaiz", label: "Maíz" },
      { key: "precioTrigo", label: "Trigo" },
    ],
  },
  insumos: {
    title: "Insumos",
    columns: [
      { key: "fecha", label: "Fecha" },
      { key: "precioSoja", label: "Soja" },
      { key: "precioMaiz", label: "Maíz" },
      { key: "precioTrigo", label: "Trigo" },
    ],
  },
  dolarDivisa: {
    title: "Dólar Divisa",
    columns: [
      { key: "fecha", label: "Fecha" },
      { key: "compra", label: "Compra" },
      { key: "venta", label: "Venta" },
    ],
  },
  pizarra: {
    title: "Pizarra",
    columns: [
      { key: "fecha", label: "Fecha" },
      { key: "precioSoja", label: "Soja" },
      { key: "precioMaiz", label: "Maíz" },
      { key: "precioTrigo", label: "Trigo" },
      { key: "precioGirasol", label: "Girasol" },
      { key: "precioSorgo", label: "Sorgo" },
    ],
  },
};
export const GeneralFilterSection = ({
  loadingPrices,
  sectionData,
  control,
  setValue,
}: Props) => {
  const handleClearDate = () => {
    setValue("generalDate", "");
  };

  const generalDate = useWatch({
    control,
    name: "generalDate",
  });

  const getRowCurrency = (value?: number) => {
    return value ? `$${formatCurrency(value, false)}` : "-";
  };

  const sectionsWithData = Object.entries(sectionData || {}).filter(
    ([, rows]) => (rows as any[]).length > 0,
  );
  return (
    <Flex flexDir="column" marginTop="30px">
      <Box paddingBottom="30px">
        {isEmpty(generalDate) && <GeneralFilterAlert />}
        <Flex gap="12px" maxWidth={"350px"}>
          <DatePicker
            name="generalDate"
            control={control}
            labelText="Fecha"
            maxDate={dayjs()}
            disablePortal={false}
            clearable={false}
            placeholder="Seleccionar"
          />
          <Button marginTop="30px" paddingX="8px" onClick={handleClearDate}>
            {" "}
            <Filter />
            Limpiar filtro
          </Button>
        </Flex>
      </Box>
      {loadingPrices && <LinearProgress />}

      {!loadingPrices && !isEmpty(generalDate) && (
        <Flex
          flexDir="column"
          overflowY="auto"
          pr="4px"
          className="card-container"
          css={{
            "@media (max-width: 1350px) and (min-width: 1272px)": {
              maxHeight: "45vh",
            },
            "@media (max-width: 1414px) and (min-width: 1351px)": {
              maxHeight: "50vh",
            },
            "@media (max-width: 1590px) and (min-width: 1415px)": {
              maxHeight: "55vh",
            },
            "@media (max-width: 1696px) and (min-width: 1591px)": {
              maxHeight: "60vh",
            },
            "@media (max-width: 1920px)": {
              maxHeight: "65vh",
            },
          }}
          gap="30px"
        >
          {sectionsWithData?.length ? (
            sectionsWithData?.map(([sectionKey, rows]) => {
              const config = TABLE_CONFIG[sectionKey];
              if (!config) return null;

              return (
                <Card.Root key={v4()} maxW="full" borderRadius="8px">
                  <Card.Body paddingX="10px" paddingY="12px" borderRadius="8px">
                    <Flex flexDir="column" gap="18px">
                      <Text className="amd-card-title" fontSize="20px">
                        {config.title}
                      </Text>

                      <Table.Root size="sm" striped borderRadius="12px">
                        <Table.Header>
                          <Table.Row>
                            {config.columns.map((col) => (
                              <Table.ColumnHeader key={col.key}>
                                <Text
                                  padding="16px"
                                  color={AppColors.GRAY_TEXT}
                                  fontWeight={700}
                                  textTransform="uppercase"
                                  fontSize="12px"
                                >
                                  {col.label}
                                </Text>
                              </Table.ColumnHeader>
                            ))}
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {(rows as any[]).map((row) => (
                            <Table.Row key={v4()}>
                              {config.columns.map((col) => (
                                <Table.Cell key={col.key}>
                                  <Text
                                    padding="16px"
                                    fontWeight={400}
                                    fontSize="12px"
                                    color={AppColors.BLACK_TEXT_SECONDARY}
                                  >
                                    {col.key === "fecha"
                                      ? dayjs(row[col.key]).format("YYYY/MM/DD")
                                      : getRowCurrency(row[col.key])}
                                  </Text>
                                </Table.Cell>
                              ))}
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    </Flex>
                  </Card.Body>
                </Card.Root>
              );
            })
          ) : (
            <NoData />
          )}
        </Flex>
      )}
    </Flex>
  );
};
